import { supabase } from '../supabase';
import { useClientStore } from '../stores/clientStore';
import { useLeadNoteStore } from '../stores/leadNoteStore';
import type { CalendarQuickOutcome } from '../types';

/** Esiti che valgono per questo tipo di task (vuoto = videochiamata, appuntamento e meeting). */
export function outcomesForType(all: readonly CalendarQuickOutcome[] | null | undefined, type: string | null | undefined): CalendarQuickOutcome[] {
  const t = type || '';
  return (all || []).filter(o => (o.activity_types?.length ? o.activity_types.includes(t) : ['videochiamata', 'appuntamento', 'meeting', 'sopralluogo'].includes(t)));
}

/** Testo dell'esito salvato sulla task (le dashboard e il MCP leggono `outcome`). */
export function outcomeText(o: CalendarQuickOutcome, note: string): string {
  const n = note.trim();
  return n ? `${o.label}: ${n}` : o.label;
}

/** Il giorno lavorativo dopo, alle 10 (ora locale): quando cade il richiamo. */
export function nextWorkingDayAt10(from: Date): Date {
  const d = new Date(from);
  d.setDate(d.getDate() + 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  return d;
}

export interface ApplyOutcomeResult { stageMoved: boolean; stageSkippedReason?: string; callbackId?: string }

/**
 * Applica un esito: completa la task, scrive l'esito, sposta il lead nella fase collegata (con lo stesso
 * aggiornamento del cambio fase a mano, che fa partire automazioni e storico), salva la nota sul lead,
 * e se previsto crea il richiamo per chi aveva fissato l'appuntamento.
 */
export async function applyQuickOutcome(params: {
  activityId: string;
  outcome: CalendarQuickOutcome;
  note: string;
  leadId?: string | null;
  creatorId?: string | null;
  meId: string;
  organizationId: string;
  activityTitle: string;
  /** Inizio dell'appuntamento: il richiamo non cade mai prima del giorno dopo l'appuntamento */
  activityStart?: string | null;
}): Promise<ApplyOutcomeResult> {
  const { activityId, outcome, note, leadId, creatorId, meId, organizationId, activityTitle, activityStart } = params;
  const now = new Date();
  // Rimandato: l'appuntamento resta aperto e si sposta al nuovo orario; gli altri esiti lo chiudono
  const closes = outcome.after !== 'reschedule';
  const { error } = await supabase
    .from('sales_activities')
    .update(closes
      ? { status: 'completed', completed_at: now.toISOString(), outcome: outcomeText(outcome, note) }
      : { outcome: outcomeText(outcome, note) })
    .eq('id', activityId);
  if (error) throw error;

  const result: ApplyOutcomeResult = { stageMoved: false };
  if (leadId && outcome.stage_id) {
    const [{ data: lead }, { data: stage }] = await Promise.all([
      supabase.from('clients').select('pipeline_id, pipeline_stage_id').eq('id', leadId).maybeSingle(),
      supabase.from('lead_pipeline_stages').select('id, pipeline_id, name').eq('id', outcome.stage_id).maybeSingle(),
    ]);
    if (!stage) result.stageSkippedReason = 'la fase collegata all\'esito non esiste più';
    else if (lead?.pipeline_id && stage.pipeline_id !== lead.pipeline_id) result.stageSkippedReason = `il lead è in un'altra pipeline, la fase "${stage.name}" non vale`;
    else if (lead?.pipeline_stage_id !== stage.id) {
      await useClientStore.getState().updateClient(leadId, { pipeline_stage_id: stage.id, last_activity_date: now.toISOString() } as any);
      result.stageMoved = true;
    }
  }

  if (leadId && note.trim()) {
    await useLeadNoteStore.getState().createNote(leadId, `${outcome.label} (${activityTitle}): ${note.trim()}`);
  }

  if (outcome.after === 'callback' && creatorId) {
    const start = activityStart ? new Date(activityStart) : null;
    const due = nextWorkingDayAt10(start && !isNaN(start.getTime()) && start > now ? start : now);
    const { data, error: cbError } = await supabase
      .from('sales_activities')
      .insert({
        organization_id: organizationId,
        user_id: meId,
        assigned_to: creatorId,
        lead_id: leadId || null,
        type: 'call',
        status: 'pending',
        title: `Richiamare: ${activityTitle}`,
        description: `Esito dell'appuntamento: ${outcomeText(outcome, note)}`,
        due_date: due.toISOString(),
        end_date: new Date(due.getTime() + 15 * 60000).toISOString(),
      })
      .select('id')
      .single();
    if (cbError) throw cbError;
    result.callbackId = data?.id;
  }
  return result;
}
